import type { StageMeta } from './workflowStages';

/**
 * One-time Construction deliberately joins two established stage bands: the
 * construction verifier (200–202) and shared authoring (604–606).  Keep this
 * list separate from Services so its discontinuous backend numbering and
 * construction-specific labels cannot alter the Services regression path.
 */
export const ONE_TIME_CONSTRUCTION_STAGE_ORDER = [200, 201, 202, 604, 605, 606] as const;
export const ONE_TIME_CONSTRUCTION_PRICE_ESTIMATOR_STAGE_ORDER = [200, 201, 202, 604, 605, 606, 623] as const;

/**
 * Requirements-based construction authoring has its own non-overlapping
 * construction-generation band. Stage 206 owns both draft generation and the
 * Manual Edit document, so it never writes verification stages 200–202.
 */
export const CONSTRUCTION_SOW_GENERATION_STAGE_ORDER = [204, 205, 206] as const;

export const oneTimeConstructionStages: StageMeta[] = [
  { id: 'construction-sow-upload', name: 'Document Upload', description: 'Upload the construction SOW/PWS and typed supporting documents' },
  { id: 'construction-sow-suggestions', name: 'Suggestions', description: 'Review construction verification results and annotations' },
  { id: 'construction-sow-manual-edit', name: 'Manual Edit', description: 'Edit and save the construction SOW before continuing to authoring' },
  { id: 'construction-salient-chars', name: 'Salient Characteristics', description: 'Extract construction scope, materials, systems, standards, and acceptance requirements' },
  { id: 'construction-code-selection', name: 'Code Selection', description: 'Select construction NAICS and PSC codes for vendor discovery' },
  { id: 'construction-vendor-discovery', name: 'Vendor Discovery', description: 'Discover vendors using the construction characteristics and selected codes' },
];

export const priceEstimatorStage: StageMeta = {
  id: 'construction-price-estimator',
  name: 'Price realism estimator',
  description: 'Review quantities and calculate a sourced construction planning estimate',
};

export const oneTimeConstructionPriceEstimatorStages: StageMeta[] = [
  ...oneTimeConstructionStages,
  priceEstimatorStage,
];

/** The standalone estimator has no upstream construction workflow stages. */
export const standaloneOneTimeConstructionPriceEstimatorStages: StageMeta[] = [priceEstimatorStage];

/** Standalone Construction SOW Generation ends after the shared SOW-generation surface. */
export const generationConstructionStages: StageMeta[] = [
  { id: 'construction-requirement-intake', name: 'Define Requirement', description: 'Describe the construction requirement and add supporting documents' },
  { id: 'construction-context-questions', name: 'Context Questions', description: 'Confirm project facts and resolve targeted clarification questions' },
  { id: 'construction-sow-manual-edit', name: 'Generate SOW', description: 'Generate, edit, and finalize the construction SOW' },
];

/**
 * The One-time workflow has two server-owned origins. Existing-SOW flows use
 * `oneTimeConstructionStages`; requirements generation uses this array before
 * converging at generation-stage 206 Manual Edit and the authoring tail.
 */
export const oneTimeConstructionGenerationStages: StageMeta[] = [
  ...generationConstructionStages,
  { id: 'construction-salient-chars', name: 'Salient Characteristics', description: 'Extract construction scope, materials, systems, standards, and acceptance requirements' },
  { id: 'construction-code-selection', name: 'Code Selection', description: 'Select construction NAICS and PSC codes for vendor discovery' },
  { id: 'construction-vendor-discovery', name: 'Vendor Discovery', description: 'Discover vendors using the construction characteristics and selected codes' },
];

export const oneTimeConstructionGenerationPriceEstimatorStages: StageMeta[] = [
  ...oneTimeConstructionGenerationStages,
  priceEstimatorStage,
];

export interface ConstructionWorkflowPathLike {
  policy_version?: unknown;
  terminal_key?: unknown;
  logical_stages?: unknown;
}

/**
 * Stage 623 follows Vendor Discovery for both current and grandfathered
 * One-time Construction descriptors. Newer backend projections explicitly
 * include the estimator stage. A legacy six-stage projection ending at Vendor
 * Discovery is the compatible predecessor and therefore also exposes its
 * estimator continuation; access is still gated by the server-derived
 * super-admin capability in App.
 */
export function hasConstructionPriceEstimatorStage(
  path: ConstructionWorkflowPathLike | null | undefined,
): boolean {
  if (!path || !Array.isArray(path.logical_stages)) return false;
  const terminal = path.logical_stages[path.logical_stages.length - 1];
  if (!terminal || typeof terminal !== 'object') return false;
  const terminalEntry = terminal as { key?: unknown; stage_code?: unknown };
  return (terminalEntry.key === 'price_estimator' && terminalEntry.stage_code === 623)
    || (terminalEntry.key === 'vendor_discovery' && terminalEntry.stage_code === 606);
}

/**
 * Returns the furthest UI index a user may resume. A completed stage unlocks
 * its successor; the terminal vendor-discovery page remains the resume target.
 */
export function resumeOneTimeConstructionStage(
  completedStages: readonly number[],
  verificationReady = true,
  includesPriceEstimator = true,
): number {
  if (includesPriceEstimator && (completedStages.includes(623) || completedStages.includes(606))) return 6;
  if (completedStages.includes(606) || completedStages.includes(605)) return 5;
  if (completedStages.includes(604)) return 4;
  if (completedStages.includes(202)) return 3;
  if (completedStages.includes(201) && verificationReady) return 2;
  // Stage 200 records independent document/context mutations. It is not proof
  // that a named SOW and a planned vehicle were both persisted, and mounting
  // Suggestions would immediately auto-start verification. Keep incomplete
  // Existing-SOW flows on Document Upload until the authoritative readback
  // confirms both prerequisites.
  if (completedStages.includes(200) && verificationReady) return 1;
  return 0;
}

/**
 * Manual Edit's confirmation must honor the stage the user selected. Keeping
 * this small mapping explicit avoids silently redirecting a Document Upload
 * selection back into Suggestions (which can auto-start verification).
 */
export function resolveOneTimeConstructionEditReturnStage(
  requestedStage: number,
): 0 | 1 {
  return requestedStage === 0 ? 0 : 1;
}
